import { useMemo, useState } from "react";
import {
  Loader2, Download, ArrowUp, ArrowDown, ArrowRight,
  Truck, CheckCircle2, PauseCircle, Wrench, UserCheck,
  Route, CalendarCheck2, Wallet, Fuel, Receipt, Landmark,
  ClipboardList, Bell, FileWarning, AlertTriangle, Info,
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import toast from "react-hot-toast";
import { useFleetDashboard } from "../hooks/useFleetDashboard";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";

// Elevation used across every card on this page — one consistent recipe.
const CARD_SHADOW = "0 1px 2px rgba(16,24,40,0.04), 0 4px 12px rgba(16,24,40,0.05)";
const CARD = { background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, boxShadow: CARD_SHADOW };
const ROW_BUTTON = {
  display: "flex", alignItems: "center", gap: 12, padding: "9px 8px",
  background: "none", border: "none", borderRadius: 6, cursor: "pointer",
  textAlign: "left", width: "100%", transition: "background 0.1s",
};

// Validated categorical palette (dataviz skill) — fixed hue order, direct-labeled
// everywhere it's used so identity never rides on color alone.
const EXPENSE_COLORS = {
  fuel: "#2a78d6", toll: "#eb6834", repair: "#1baf7a",
  insurance_premium: "#eda100", salary: "#e87ba4", other: "#008300",
};
// Vehicle status colors — reused verbatim from VehicleCard's status vocabulary
// so a vehicle's color means the same thing everywhere in the app.
const UTILIZATION_COLORS = { active: "#16A34A", idle: "#D97706", inMaintenance: "#DC2626", offline: "#9CA3AF" };

function daysUntil(iso) {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

function timeAgo(iso) {
  const diff  = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  < 1)  return "just now";
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

function greetingWord() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

// Status colors always ship with an icon + label, never color alone.
const SEVERITY_META = {
  critical: { label: "Overdue",  color: "#DC2626", bg: "#FEF2F2", tint: "#FCA5A5", weight: 15 },
  warning:  { label: "Due Soon", color: "#D97706", bg: "#FFFBEB", tint: "#FCD34D", weight: 8 },
  notice:   { label: "Upcoming", color: "#2563EB", bg: "#EFF6FF", tint: "#93C5FD", weight: 3 },
};

function severityFor(days) {
  if (days < 0) return "critical";
  if (days <= 7) return "warning";
  return "notice";
}

function subtitleFor(days, verb) {
  if (days < 0) return `${verb.past} ${Math.abs(days)}d ago`;
  if (days === 0) return `${verb.present} today`;
  return `${verb.present} in ${days}d`;
}

// Merges documents/maintenance/licenses into one weighted score — every point
// lost traces back to a real, visible item, same principle as driver scorecards.
function buildNeedsAttention(data) {
  const items = [];
  data.documentsExpiring.forEach(d => items.push({ severity: severityFor(daysUntil(d.expiryDate)) }));
  data.maintenanceDue.forEach(m => items.push({ severity: severityFor(daysUntil(m.dueDate)) }));
  data.driverLicensesExpiring.forEach(d => items.push({ severity: severityFor(daysUntil(d.licenseExpiry)) }));
  return items;
}

function computeHealthScore(items) {
  const score = 100 - items.reduce((sum, item) => sum + SEVERITY_META[item.severity].weight, 0);
  return Math.max(0, Math.round(score));
}

function healthBand(score) {
  if (score >= 90) return { text: "Excellent", color: "#16A34A", tint: "#DCFCE7" };
  if (score >= 70) return { text: "Good", color: "#2563EB", tint: "#DBEAFE" };
  if (score >= 40) return { text: "Needs Attention", color: "#D97706", tint: "#FEF3C7" };
  return { text: "Critical", color: "#DC2626", tint: "#FEE2E2" };
}

function trend(current, previous) {
  if (current === previous) return null;
  if (previous === 0) return { pct: 100, up: true };
  const pct = Math.round(((current - previous) / previous) * 100);
  return { pct: Math.abs(pct), up: pct > 0 };
}

// For cost figures, up = worse (red), down = better (green) — the reverse of
// how a trend arrow usually reads.
function TrendBadge({ current, previous }) {
  const t = trend(current, previous);
  if (!t) return <span style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 600 }}>No change vs last month</span>;
  const color = t.up ? "#DC2626" : "#16A34A";
  const Icon = t.up ? ArrowUp : ArrowDown;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11.5, fontWeight: 700, color }}>
      <Icon size={11} /> {t.pct}% vs last month
    </span>
  );
}

function EmptyMini({ text, icon: Icon = Info, good = false }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: good ? "#16A34A" : "#9CA3AF", fontWeight: good ? 600 : 500, padding: "6px 0" }}>
      <Icon size={14} /> {text}
    </div>
  );
}

function PanelHeader({ title, onViewAll }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
      <div style={{ fontSize: 14.5, fontWeight: 700, color: "#111827" }}>{title}</div>
      {onViewAll && (
        <button
          onClick={onViewAll}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#4F46E5" }}
        >
          View All
        </button>
      )}
    </div>
  );
}

/* ── KPI tile — plain count, small context line, no trend ── */
function KpiTile({ icon: Icon, accent, label, value, sub, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      style={{
        ...CARD, flex: "1 1 170px", padding: "14px 16px", textAlign: "left",
        border: `1px solid #E5E7EB`, cursor: onClick ? "pointer" : "default",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8, flexShrink: 0,
          background: accent + "17", color: accent,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}><Icon size={14} /></div>
        <span style={{ fontSize: 12, color: "#6B7280", fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#111827", letterSpacing: -0.3 }}>{value}</div>
      {sub && <div style={{ marginTop: 5, fontSize: 11.5, color: "#9CA3AF" }}>{sub}</div>}
    </Tag>
  );
}

/* ── Metric tile — currency or count, with trend badge / subtitle ── */
function MetricTile({ icon: Icon, accent, label, value, prev, isCurrency = true, sub, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      style={{ ...CARD, flex: "1 1 170px", padding: "14px 16px", textAlign: "left", cursor: onClick ? "pointer" : "default" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8, flexShrink: 0,
          background: accent + "17", color: accent,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}><Icon size={14} /></div>
        <span style={{ fontSize: 12, color: "#6B7280", fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: 21, fontWeight: 700, color: "#111827", letterSpacing: -0.3 }}>
        {isCurrency ? `₹${value.toLocaleString("en-IN")}` : value}
      </div>
      <div style={{ marginTop: 5 }}>
        {prev !== undefined ? <TrendBadge current={value} previous={prev} /> : <span style={{ fontSize: 11, color: "#9CA3AF" }}>{sub}</span>}
      </div>
    </Tag>
  );
}

/* ── Donut card — chart + direct-labeled legend, never color alone ── */
function DonutCard({ title, data, centerValue, centerLabel, formatLegendValue }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div style={{ ...CARD, padding: "18px 20px", flex: "1 1 320px" }}>
      <div style={{ fontSize: 14.5, fontWeight: 700, color: "#111827", marginBottom: 14 }}>{title}</div>
      {total === 0 ? (
        <EmptyMini text="No data yet this month" />
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <div style={{ width: 140, height: 140, position: "relative", flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="label" innerRadius={44} outerRadius={66} paddingAngle={2} stroke="#fff" strokeWidth={2}>
                  {data.map(d => <Cell key={d.key} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [formatLegendValue(v), n]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E5E7EB" }} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}>{centerValue}</div>
              <div style={{ fontSize: 10, color: "#9CA3AF" }}>{centerLabel}</div>
            </div>
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
            {data.map(d => (
              <div key={d.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: d.color, flexShrink: 0 }} />
                <span style={{ flex: 1, color: "#374151", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.label}</span>
                <span style={{ color: "#111827", fontWeight: 700 }}>{Math.round((d.value / total) * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ExpenseTrendCard({ data }) {
  const hasData = data.some(d => d.fuel + d.other > 0);
  return (
    <div style={{ ...CARD, padding: "18px 20px", flex: "1 1 380px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: "#111827" }}>Monthly Expense Trend</div>
        <div style={{ display: "flex", gap: 14 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6B7280", fontWeight: 600 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: EXPENSE_COLORS.fuel }} /> Fuel
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6B7280", fontWeight: 600 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: EXPENSE_COLORS.insurance_premium }} /> Other
          </span>
        </div>
      </div>
      {!hasData ? <EmptyMini text="No expenses logged in the last 6 months" /> : (
        <ResponsiveContainer width="100%" height={210}>
          <BarChart data={data} barGap={4} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11.5, fill: "#9CA3AF" }} />
            <YAxis
              tickLine={false} axisLine={false} width={44}
              tick={{ fontSize: 11, fill: "#9CA3AF" }}
              tickFormatter={v => (v >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`)}
            />
            <Tooltip
              formatter={(v, name) => [`₹${Number(v).toLocaleString("en-IN")}`, name === "fuel" ? "Fuel" : "Other"]}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E5E7EB" }}
            />
            <Bar dataKey="fuel" stackId="a" fill={EXPENSE_COLORS.fuel} />
            <Bar dataKey="other" stackId="a" fill={EXPENSE_COLORS.insurance_premium} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

const ALERT_ICON = {
  doc_expiry: FileWarning, document_expiry: FileWarning,
  maintenance_due: Wrench, maintenance_approved: Wrench, maintenance_rejected: Wrench,
  vehicle_assigned: Truck, vehicle_changed: Truck,
  trip_assigned: Route, trip_cancelled: Route,
  fuel_approved: Fuel, fuel_rejected: Fuel,
  expense_approved: Receipt, expense_rejected: Receipt,
  leave_approved: CalendarCheck2, leave_rejected: CalendarCheck2,
  inspection_flagged: AlertTriangle,
  driver_request_submitted: ClipboardList,
  password_reset: Info,
};
const ALERT_TAB = {
  doc_expiry: "documents", document_expiry: "documents",
  maintenance_due: "maintenance", maintenance_approved: "maintenance", maintenance_rejected: "maintenance",
  fuel_approved: "fuel", fuel_rejected: "fuel",
  expense_approved: "expenses", expense_rejected: "expenses",
  trip_assigned: "trips", trip_cancelled: "trips",
  driver_request_submitted: "requests", leave_approved: "requests", leave_rejected: "requests", inspection_flagged: "requests",
  vehicle_assigned: "drivers", vehicle_changed: "drivers",
};

function RecentAlertsPanel({ alerts, onNavigate }) {
  return (
    <div style={{ ...CARD, padding: "18px 20px", flex: "1 1 300px" }}>
      <PanelHeader title="Recent Alerts" />
      {alerts.length === 0 ? (
        <EmptyMini text="No alerts — you're all caught up" icon={CheckCircle2} good />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {alerts.map(a => {
            const Icon = ALERT_ICON[a.type] || Bell;
            const tab = ALERT_TAB[a.type];
            return (
              <button key={a.id} onClick={() => tab && onNavigate?.(tab)} style={{ ...ROW_BUTTON, cursor: tab ? "pointer" : "default" }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: "#EEF2FF", color: "#4F46E5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={14} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title}</div>
                  <div style={{ fontSize: 11.5, color: "#9CA3AF" }}>{timeAgo(a.createdAt)}</div>
                </div>
                {!a.isRead && <span style={{ width: 7, height: 7, borderRadius: 99, background: "#4F46E5", flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DocExpiryPanel({ items, vehicles, onOpenVehicleProfile, onNavigate }) {
  return (
    <div style={{ ...CARD, padding: "18px 20px", flex: "1 1 300px" }}>
      <PanelHeader title="Upcoming Document Expiry" onViewAll={() => onNavigate?.("documents")} />
      {items.length === 0 ? (
        <EmptyMini text="All documents are current" icon={CheckCircle2} good />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {items.slice(0, 5).map(d => {
            const days = daysUntil(d.expiryDate);
            const meta = SEVERITY_META[severityFor(days)];
            return (
              <button
                key={d.id}
                onClick={() => {
                  const v = vehicles.find(x => x.id === d.vehicleId);
                  if (v && onOpenVehicleProfile) onOpenVehicleProfile(v);
                  else onNavigate?.("documents");
                }}
                style={{ ...ROW_BUTTON, borderLeft: `3px solid ${meta.tint}`, paddingLeft: 10 }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {d.docType.toUpperCase()} — {d.vehicleLabel}
                  </div>
                  <div style={{ fontSize: 11.5, color: meta.color, fontWeight: 600, marginTop: 1 }}>
                    {subtitleFor(days, { past: "Expired", present: "Expires" })}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TopVehiclesPanel({ items, vehicles, onOpenVehicleProfile }) {
  const max = Math.max(1, ...items.map(i => i.tripCount));
  return (
    <div style={{ ...CARD, padding: "18px 20px", flex: "1 1 300px" }}>
      <PanelHeader title="Top Vehicles by Trips" />
      {items.length === 0 ? (
        <EmptyMini text="No completed trips yet this month" />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {items.map(v => (
            <button
              key={v.id}
              onClick={() => {
                const full = vehicles.find(x => x.id === v.id);
                if (full && onOpenVehicleProfile) onOpenVehicleProfile(full);
              }}
              style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", padding: 0, width: "100%" }}
            >
              <div style={{ width: 28, height: 28, borderRadius: 8, background: "#F3F4F6", color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Truck size={13} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.label}</span>
                  <span style={{ fontWeight: 700, color: "#4F46E5", flexShrink: 0, marginLeft: 8 }}>{v.tripCount} trip{v.tripCount === 1 ? "" : "s"}</span>
                </div>
                <div style={{ height: 6, borderRadius: 99, background: "#F3F4F6", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${(v.tripCount / max) * 100}%`, borderRadius: 99, background: "#4F46E5" }} />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function currentMonthValue() {
  return new Date().toISOString().slice(0, 7);
}

export default function FleetDashboard({ onNavigate, vehicles = [], onOpenVehicleProfile }) {
  const { data, loading } = useFleetDashboard();
  const { session } = useAuth();
  const [reportMonth, setReportMonth] = useState(currentMonthValue());
  const [downloading, setDownloading] = useState(false);

  const needsAttention = useMemo(() => (data ? buildNeedsAttention(data) : []), [data]);
  const healthScore = useMemo(() => computeHealthScore(needsAttention), [needsAttention]);
  const health = healthBand(healthScore);

  const expenseDonut = useMemo(() => {
    if (!data) return [];
    return data.expenseBreakdown.map(e => ({ key: e.category, label: e.label, value: e.amount, color: EXPENSE_COLORS[e.category] || "#9CA3AF" }));
  }, [data]);

  const utilizationDonut = useMemo(() => {
    if (!data) return [];
    const vc = data.vehicleCounts;
    return [
      { key: "active", label: "Moving", value: vc.active, color: UTILIZATION_COLORS.active },
      { key: "idle", label: "Idle", value: vc.idle, color: UTILIZATION_COLORS.idle },
      { key: "inMaintenance", label: "In Maintenance", value: vc.inMaintenance, color: UTILIZATION_COLORS.inMaintenance },
      { key: "offline", label: "Inactive", value: vc.offline, color: UTILIZATION_COLORS.offline },
    ].filter(d => d.value > 0);
  }, [data]);

  async function handleDownloadReport() {
    setDownloading(true);
    try {
      const res = await api.get(`/api/fleet/report?month=${reportMonth}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `fleet-report-${reportMonth}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download report. Please try again.");
    } finally {
      setDownloading(false);
    }
  }

  if (loading || !data) {
    return (
      <main style={{ flex: 1, overflow: "auto", background: "#F9FAFB", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "#9CA3AF", display: "flex", alignItems: "center", gap: 8 }}>
          <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Loading fleet dashboard…
        </div>
      </main>
    );
  }

  // A dashboard with zero vehicles has nothing to summarize — show the
  // onboarding step that's actually true, instead of an all-₹0 dashboard
  // that reads as broken rather than simply empty.
  if (vehicles.length === 0) {
    return (
      <main style={{ flex: 1, overflow: "auto", background: "#F9FAFB", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ ...CARD, maxWidth: 440, width: "100%", padding: "36px 32px", textAlign: "center" }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14, background: "#EEF2FF", color: "#4F46E5",
            display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px",
          }}><Truck size={26} /></div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 8 }}>Add your first vehicle</div>
          <div style={{ fontSize: 13.5, color: "#6B7280", lineHeight: 1.6, marginBottom: 22 }}>
            The Fleet Dashboard summarizes vehicles, trips, documents, maintenance, and cost across your fleet —
            it'll fill in as soon as you add a vehicle to track.
          </div>
          <button
            onClick={() => onNavigate?.("tracking")}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "#4F46E5", border: "none", borderRadius: 9,
              padding: "0 20px", height: 42, cursor: "pointer",
              fontSize: 13.5, fontWeight: 700, color: "#fff",
            }}
          >
            Go to Tracking <ArrowRight size={14} />
          </button>
        </div>
      </main>
    );
  }

  const vc = data.vehicleCounts;
  const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);

  return (
    <main style={{ flex: 1, overflow: "auto", background: "#F9FAFB" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "28px 24px 40px" }}>

        {/* ── Page header ── */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 22, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 21, fontWeight: 700, color: "#111827", letterSpacing: -0.3 }}>
              {greetingWord()}, {session?.name || "there"}! 👋
            </div>
            <div style={{ fontSize: 13.5, color: "#6B7280", marginTop: 3 }}>
              Here's what's happening with your fleet today.
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="month"
                value={reportMonth}
                onChange={e => setReportMonth(e.target.value)}
                style={{ height: 38, borderRadius: 9, border: "1px solid #E5E7EB", background: "#fff", fontSize: 13, color: "#374151", padding: "0 10px" }}
              />
              <button
                onClick={handleDownloadReport}
                disabled={downloading}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: downloading ? "#9CA3AF" : "#4F46E5", border: "none", borderRadius: 9,
                  padding: "0 16px", height: 38, cursor: downloading ? "default" : "pointer",
                  fontSize: 13.5, fontWeight: 700, color: "#fff",
                  boxShadow: downloading ? "none" : "0 1px 2px rgba(79,70,229,0.25)",
                }}
              >
                {downloading ? <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> : <Download size={14} />}
                Download Report
              </button>
            </div>
            <div style={{ fontSize: 11.5, color: "#9CA3AF" }}>Also emailed to you automatically on the 1st of each month</div>
          </div>
        </div>

        {/* ── Vehicle & driver counts ── */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <KpiTile icon={Truck} accent="#4F46E5" label="Total Vehicles" value={vc.total} sub="All vehicles" />
          <KpiTile icon={CheckCircle2} accent="#16A34A" label="Active Vehicles" value={vc.active} sub={`${pct(vc.active, vc.total)}% of total`} />
          <KpiTile icon={PauseCircle} accent="#D97706" label="Idle Vehicles" value={vc.idle} sub={`${pct(vc.idle, vc.total)}% of total`} />
          <KpiTile icon={Wrench} accent="#DC2626" label="In Maintenance" value={vc.inMaintenance} sub={`${pct(vc.inMaintenance, vc.total)}% of total`} onClick={() => onNavigate?.("maintenance")} />
          <KpiTile icon={UserCheck} accent="#2563EB" label="Drivers On Duty" value={data.driversOnDuty} sub={`of ${data.totalDrivers} active driver${data.totalDrivers === 1 ? "" : "s"}`} onClick={() => onNavigate?.("drivers")} />
        </div>

        {/* ── Fleet Overview banner ── */}
        <div style={{
          ...CARD, padding: "22px 26px", marginBottom: 16, overflow: "hidden", position: "relative",
          background: `linear-gradient(135deg, ${health.tint}55 0%, #fff 45%)`,
        }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: health.color }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 20 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
              <span style={{ fontSize: 42, fontWeight: 700, color: health.color, letterSpacing: -1, lineHeight: 1 }}>{healthScore}</span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Fleet Health — {health.text}</div>
                <div style={{ fontSize: 12.5, color: "#6B7280", marginTop: 2 }}>
                  {needsAttention.length === 0 ? "Nothing needs attention right now" : `${needsAttention.length} item${needsAttention.length === 1 ? "" : "s"} need attention`}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {[
                { label: "Vehicles Moving", value: vc.active, color: "#16A34A" },
                { label: "Trips In Progress", value: data.tripsRunning, color: "#4F46E5" },
                { label: "Docs Expiring Soon", value: data.documentsExpiringCount, color: "#D97706" },
              ].map(pill => (
                <div key={pill.label} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, padding: "8px 14px", minWidth: 140 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 7, height: 7, borderRadius: 99, background: pill.color }} />
                    <span style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>{pill.value}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{pill.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Operational + financial metrics ── */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <MetricTile icon={Route} accent="#4F46E5" label="Trips Running" value={data.tripsRunning} isCurrency={false} sub="Live now" onClick={() => onNavigate?.("trips")} />
          <MetricTile icon={CalendarCheck2} accent="#16A34A" label="Trips Completed Today" value={data.tripsCompletedToday} isCurrency={false} sub="Completed today" onClick={() => onNavigate?.("trips")} />
          <MetricTile icon={Wallet} accent="#4B5563" label="Monthly Expenses" value={data.totalOperatingCostThisMonth} prev={data.totalOperatingCostLastMonth} onClick={() => onNavigate?.("expenses")} />
          <MetricTile icon={Fuel} accent="#2563EB" label="Fuel Expenses" value={data.fuelCostThisMonth} prev={data.fuelCostLastMonth} onClick={() => onNavigate?.("fuel")} />
          <MetricTile icon={Landmark} accent="#D97706" label="Toll Expenses" value={data.tollCostThisMonth} prev={data.tollCostLastMonth} onClick={() => onNavigate?.("expenses")} />
          <MetricTile icon={Wrench} accent="#DC2626" label="Pending Maintenance" value={data.pendingMaintenanceCount} isCurrency={false} sub="Needs action" onClick={() => onNavigate?.("maintenance")} />
          <MetricTile icon={ClipboardList} accent="#7C3AED" label="Pending Requests" value={data.pendingRequestsCount} isCurrency={false} sub="Awaiting review" onClick={() => onNavigate?.("requests")} />
        </div>

        {/* ── Charts ── */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <DonutCard
            title="Expenses Breakdown"
            data={expenseDonut}
            centerValue={`₹${(data.totalOperatingCostThisMonth / 1000).toFixed(0)}k`}
            centerLabel="Total Expenses"
            formatLegendValue={v => `₹${Number(v).toLocaleString("en-IN")}`}
          />
          <ExpenseTrendCard data={data.monthlyExpenseTrend} />
          <DonutCard
            title="Vehicle Utilization"
            data={utilizationDonut}
            centerValue={`${pct(vc.active, vc.total)}%`}
            centerLabel="Moving"
            formatLegendValue={v => `${v} vehicle${v === 1 ? "" : "s"}`}
          />
        </div>

        {/* ── Lists ── */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <RecentAlertsPanel alerts={data.recentAlerts} onNavigate={onNavigate} />
          <DocExpiryPanel items={data.documentsExpiring} vehicles={vehicles} onOpenVehicleProfile={onOpenVehicleProfile} onNavigate={onNavigate} />
          <TopVehiclesPanel items={data.topVehiclesByTrips} vehicles={vehicles} onOpenVehicleProfile={onOpenVehicleProfile} />
        </div>
      </div>
    </main>
  );
}
